/**
 * Business Proof Points — the targets that define success. Validated and
 * aspirational items are visually distinguished (an unconfirmed proof point is
 * a hope, not a fact).
 */

import { useState } from 'react';
import { Plus, Pencil, BadgeCheck, CircleDashed } from 'lucide-react';
import type { AssembledPor, PorProofPoint } from '../../types/plan-of-record';
import { createProofPoint, updateProofPoint, deleteProofPoint } from '../../lib/planOfRecordService';
import { GhostButton, PorCard, PorCardHeader, PorEmptyState, PorModal, PrimaryButton } from './porUi';
import { PorNarrativeMarkdown } from './PorNarrativeMarkdown';

export function ProofPointsPanel({
  assembled,
  refresh,
}: {
  assembled: AssembledPor;
  refresh: () => Promise<void>;
}) {
  const [editing, setEditing] = useState<PorProofPoint | 'new' | null>(null);
  const points = assembled.proof_points;

  return (
    <PorCard>
      <PorCardHeader
        title="Business Proof Points"
        count={points.length}
        action={
          <GhostButton onClick={() => setEditing('new')}>
            <span className="inline-flex items-center gap-1">
              <Plus className="h-3.5 w-3.5" /> Add
            </span>
          </GhostButton>
        }
      />

      {points.length === 0 ? (
        <PorEmptyState
          icon={<BadgeCheck className="h-6 w-6" />}
          title="No proof points yet"
          prompt="What targets define success for this plan? Mark each as validated or aspirational — be honest about which is which."
          action={<GhostButton onClick={() => setEditing('new')}>Add a proof point</GhostButton>}
        />
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {points.map((p) => (
            <div
              key={p.id}
              className="group relative border border-[var(--rule)] p-3 hover:border-[var(--rule)] transition-colors"
            >
              <button
                onClick={() => setEditing(p)}
                className="absolute top-2 right-2 text-[var(--text-ter)] hover:text-[var(--text-sec)] opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Edit proof point"
              >
                <Pencil className="h-3.5 w-3.5" />
              </button>
              <div className="flex items-baseline gap-2">
                <span className="text-xl font-semibold text-[var(--text-pri)] tabular-nums">{p.value_display}</span>
                {p.confirmed ? (
                  <BadgeCheck className="h-4 w-4 text-primary-600" />
                ) : (
                  <CircleDashed className="h-4 w-4 text-[var(--text-ter)]" />
                )}
              </div>
              <p className="text-sm text-[var(--text-sec)] mt-0.5">{p.label}</p>
              {p.context && (
                <div className="mt-0.5">
                  <PorNarrativeMarkdown source={p.context} />
                </div>
              )}
              <p
                className={`text-[10px] font-mono uppercase tracking-wide mt-1 ${
                  p.confirmed ? 'text-primary-600' : 'text-[var(--text-ter)]'
                }`}
              >
                {p.confirmed ? 'validated' : 'aspirational'}
              </p>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ProofPointModal
          porId={assembled.id}
          point={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      )}
    </PorCard>
  );
}

function ProofPointModal({
  porId,
  point,
  onClose,
  onDone,
}: {
  porId: string;
  point: PorProofPoint | null;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const [valueDisplay, setValueDisplay] = useState(point?.value_display ?? '');
  const [pointLabel, setPointLabel] = useState(point?.label ?? '');
  const [context, setContext] = useState(point?.context ?? '');
  const [confirmed, setConfirmed] = useState(point?.confirmed ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const valid = valueDisplay.trim().length > 0 && pointLabel.trim().length > 0;

  const handleSave = async () => {
    if (!valid) {
      setError('Value and label are both required.');
      return;
    }
    setBusy(true);
    setError(null);
    const payload = {
      value_display: valueDisplay.trim(),
      label: pointLabel.trim(),
      context: context.trim() || null,
      confirmed,
    };
    const { error: err } = point
      ? await updateProofPoint(point.id, payload)
      : await createProofPoint(porId, payload);
    if (err) {
      setError(err);
      setBusy(false);
      return;
    }
    setBusy(false);
    await onDone();
  };

  const handleDelete = async () => {
    if (!point) return;
    setBusy(true);
    const { error: err } = await deleteProofPoint(point.id);
    if (err) {
      setError(err);
      setBusy(false);
      return;
    }
    setBusy(false);
    await onDone();
  };

  const field = 'w-full text-sm  border border-[var(--rule)] px-3 py-2 focus:border-[var(--slate)] focus:ring-2 focus:ring-indigo-100 focus:outline-none';
  const label = 'block text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5';

  return (
    <PorModal
      title={point ? 'Edit proof point' : 'Add proof point'}
      onClose={onClose}
      footer={
        <>
          {point && (
            <button
              onClick={handleDelete}
              disabled={busy}
              className="mr-auto text-sm font-medium text-luminous-coral-700 hover:text-luminous-coral-800 disabled:opacity-40"
            >
              Delete
            </button>
          )}
          <GhostButton onClick={onClose} disabled={busy}>
            Cancel
          </GhostButton>
          <PrimaryButton onClick={handleSave} disabled={busy || !valid}>
            {busy ? 'Saving…' : 'Save'}
          </PrimaryButton>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className={label}>Value</label>
            <input
              value={valueDisplay}
              onChange={(e) => setValueDisplay(e.target.value)}
              placeholder='"5", "90%+"'
              className={field}
              autoFocus
            />
          </div>
          <div className="col-span-2">
            <label className={label}>Label</label>
            <input value={pointLabel} onChange={(e) => setPointLabel(e.target.value)} className={field} />
          </div>
        </div>
        <div>
          <label className={label}>Context</label>
          <input
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder='e.g. "Year 1 target", "Renewal rate"'
            className={field}
          />
        </div>
        <label className="flex items-center gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            className="rounded border-[var(--rule)] text-primary-600 focus:ring-primary-200"
          />
          <span className="text-sm text-[var(--text-sec)]">
            Validated — this is confirmed, not aspirational.
          </span>
        </label>
        {error && <p className="text-xs text-luminous-coral-700">{error}</p>}
      </div>
    </PorModal>
  );
}
